import { useRef, useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { useTranslation } from 'react-i18next';
import { supportAPI } from '@/lib/api/support';
import { useGlobalWebSocket } from '@/contexts/GlobalWebSocketContext';

export interface SupportMessage {
  id: number;
  content: string;
  is_from_admin?: boolean;
  attachment_url?: string;
  created_at?: string;
}

export interface SupportTicket {
  id: number;
  status?: string;
  subject?: string;
  created_at?: string;
}

// API Response interfaces for type safety
interface TicketListResponse {
  tickets?: SupportTicket[];
}

interface WebSocketNotificationData {
  type?: string;
  notification?: {
    data?: {
      ticket_id?: number;
    };
  };
}

interface TicketDetailsResponse {
  messages?: SupportMessage[];
}

export interface UseSupportChatOptions {
  onError?: (error: Error) => void;
  onMessageSent?: () => void;
}

export interface UseSupportChatReturn {
  // State
  ticketId: number | null;
  publicToken: string | null;
  tickets: SupportTicket[];
  messages: SupportMessage[];
  inputValue: string;
  setInputValue: (v: string) => void;
  file: File | null;
  setFile: (f: File | null) => void;
  loading: boolean;
  guestEmail: string;
  setGuestEmail: (v: string) => void;
  guestName: string;
  setGuestName: (v: string) => void;
  guestValidated: boolean;
  isGuest: boolean;

  // Actions
  selectTicket: (id: number) => Promise<void>;
  handleSend: () => Promise<void>;
  initGuestTicket: () => void;
  scrollRef: React.RefObject<HTMLDivElement>;
}

export function useSupportChat(options: UseSupportChatOptions = {}): UseSupportChatReturn {
  const { onError, onMessageSent } = options;
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation('common');
  const { subscribe } = useGlobalWebSocket();

  const [ticketId, setTicketId] = useState<number | null>(null);
  const [publicToken, setPublicToken] = useState<string | null>(null);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [guestEmail, setGuestEmail] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestValidated, setGuestValidated] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const isGuest = !user;

  // Scroll to bottom helper
  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'auto'): void => {
    setTimeout(() => {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior });
    }, 0);
  }, []);

  // Load tickets for logged-in users
  useEffect(() => {
    if (!user) return;
    const load = async (): Promise<void> => {
      try {
        const listRes = (await supportAPI.listMyTickets({ limit: 20 })) as TicketListResponse;
        const list = listRes.tickets ?? [];
        setTickets(list);
      } catch (e: unknown) {
        const error = e instanceof Error ? e : new Error(String(e));
        toast({ title: t('support.error'), description: error.message || t('support.loadFailed'), variant: 'destructive' });
        onError?.(error);
      }
    };
    void load();
  }, [user, toast, t, onError]);

  // Handler for WebSocket notifications - extracted to avoid promise-in-callback issues
  const handleWebSocketNotification = useCallback(
    async (data: unknown, currentTicketId: number): Promise<void> => {
      try {
        const wsData = data as WebSocketNotificationData;
        if (wsData.type === 'notification' && wsData.notification?.data?.ticket_id === currentTicketId) {
          const details = (await supportAPI.getTicket(currentTicketId)) as TicketDetailsResponse;
          setMessages(details.messages ?? []);
          scrollToBottom();
        }
      } catch (e: unknown) {
        console.error('[Support] WebSocket update failed:', e);
      }
    },
    [scrollToBottom]
  );

  // Live-Updates via WebSocket for logged-in users
  useEffect(() => {
    if (!user) return;
    if (!ticketId) return;
    const currentTicketId = ticketId;
    const unsubscribe = subscribe((data: unknown): void => {
      void handleWebSocketNotification(data, currentTicketId);
    });
    return () => {
      try {
        unsubscribe();
      } catch {
        /* noop */
      }
    };
  }, [user, ticketId, subscribe, handleWebSocketNotification]);

  // Guests only: they have a public_token but no authenticated WS session,
  // so REST polling is the only way to receive admin replies. Authenticated
  // users use the WebSocket subscribe above.
  useEffect(() => {
    if (user) return;
    if (!guestValidated || !ticketId) return;
    let cancelled = false;
    let errorCount = 0;
    const currentTicketId = ticketId;
    const currentToken = publicToken ?? undefined;

    const pollMessages = async (): Promise<void> => {
      try {
        if (cancelled) return;
        const details = (await supportAPI.getTicket(currentTicketId, currentToken)) as TicketDetailsResponse;
        setMessages(details.messages ?? []);
        errorCount = 0; // Reset on success
      } catch (e: unknown) {
        errorCount++;
        // Only show toast after 3 consecutive failures to avoid spamming
        if (errorCount === 3) {
          toast({ title: t('support.error'), description: t('support.connectionError'), variant: 'destructive' });
        }
        console.error('[Support] Polling failed:', e);
      }
    };

    const interval = window.setInterval((): void => {
      void pollMessages();
    }, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [user, guestValidated, ticketId, publicToken, toast, t]);

  const selectTicket = useCallback(
    async (tid: number): Promise<void> => {
      try {
        setTicketId(tid);
        const details = (await supportAPI.getTicket(tid, user ? undefined : publicToken ?? undefined)) as TicketDetailsResponse;
        setMessages(details.messages ?? []);
        scrollToBottom();
      } catch (e: unknown) {
        const error = e instanceof Error ? e : new Error(String(e));
        toast({ title: t('support.error'), description: error.message || t('support.loadFailed'), variant: 'destructive' });
        onError?.(error);
      }
    },
    [user, publicToken, toast, t, onError, scrollToBottom]
  );

  const handleSend = useCallback(async (): Promise<void> => {
    if (!inputValue.trim() && !file) return;
    setLoading(true);

    // Optimistic UI update
    const optimisticMessage: SupportMessage = {
      id: Date.now(),
      content: inputValue || (file ? `(${t('support.file')}: ${file.name})` : ''),
      is_from_admin: false,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimisticMessage]);
    const contentToSend = inputValue;
    setInputValue('');

    try {
      let activeTicketId = ticketId;
      let tokenForGuest: string | undefined = user ? undefined : publicToken ?? undefined;

      // Create ticket on first message if none exists
      if (!activeTicketId) {
        if (user) {
          const res = await supportAPI.createOrGetTicket({ subject: t('support.defaultSubject') });
          activeTicketId = res.id;
          setTicketId(res.id);
          setPublicToken(res.public_token ?? null);
          // Refresh ticket list
          try {
            const listRes = (await supportAPI.listMyTickets({ limit: 20 })) as TicketListResponse;
            setTickets(listRes.tickets ?? []);
          } catch (e: unknown) {
            console.error('[Support] Failed to refresh ticket list:', e);
          }
        } else {
          // Guest flow
          if (!guestEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail)) {
            throw new Error(t('support.invalidEmail'));
          }
          const payload: { subject?: string; guest_email: string; guest_name?: string } = {
            subject: t('support.defaultSubject'),
            guest_email: guestEmail,
          };
          if (guestName) payload.guest_name = guestName;
          const res = await supportAPI.createOrGetTicket(payload);
          activeTicketId = res.id;
          setTicketId(res.id);
          const token = res.public_token ?? null;
          setPublicToken(token);
          tokenForGuest = token ?? undefined;
          setGuestValidated(true);
        }
      }

      if (!activeTicketId) return;

      // Send message
      if (file) {
        await supportAPI.sendMessageWithFile(activeTicketId, file, contentToSend || '', tokenForGuest);
        setFile(null);
      } else {
        await supportAPI.sendMessage(activeTicketId, contentToSend, tokenForGuest);
      }

      // Refresh messages
      try {
        const details = (await supportAPI.getTicket(activeTicketId, tokenForGuest)) as TicketDetailsResponse;
        setMessages(details.messages ?? []);
      } catch (e: unknown) {
        // Message was sent but we couldn't fetch the updated list - inform user
        toast({ title: t('support.messageSent'), description: t('support.refreshHint'), variant: 'default' });
        console.error('[Support] Failed to refresh messages after send:', e);
      }

      onMessageSent?.();
    } catch (e: unknown) {
      const error = e instanceof Error ? e : new Error(String(e));
      toast({ title: t('support.error'), description: error.message || t('support.sendFailed'), variant: 'destructive' });
      onError?.(error);
    } finally {
      setLoading(false);
      scrollToBottom('smooth');
    }
  }, [
    inputValue,
    file,
    ticketId,
    user,
    publicToken,
    guestEmail,
    guestName,
    t,
    toast,
    onError,
    onMessageSent,
    scrollToBottom,
  ]);

  const initGuestTicket = useCallback((): void => {
    if (!guestEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail)) {
      toast({ title: t('support.emailRequired'), description: t('support.invalidEmail'), variant: 'destructive' });
      return;
    }
    setGuestValidated(true);
  }, [guestEmail, toast, t]);

  return {
    // State
    ticketId,
    publicToken,
    tickets,
    messages,
    inputValue,
    setInputValue,
    file,
    setFile,
    loading,
    guestEmail,
    setGuestEmail,
    guestName,
    setGuestName,
    guestValidated,
    isGuest,

    // Actions
    selectTicket,
    handleSend,
    initGuestTicket,
    scrollRef,
  };
}
